<template>
  <div class="share-page" :class="{ 'mobile': isMobile, 'desktop': !isMobile }">
    <div v-if="status === 'redirecting'" class="state-block">
      <h1>パートナー共有</h1>
      <p>LINE 認証画面に移動しています…</p>
    </div>
    <div v-else-if="status === 'invalid'" class="state-block">
      <h1>リンクが無効です</h1>
      <p>{{ errorMessage }}</p>
      <p class="hint">アプリで再度、パートナー共有リンクを発行してください。</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShareView',
  data() {
    return {
      isMobile: false,
      status: 'redirecting',
      errorMessage: ''
    }
  },
  mounted() {
    document.title = 'パートナー共有 | メノア'
    this.checkScreenSize()
    window.addEventListener('resize', this.checkScreenSize)
    this.startOAuthFlow()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
    },
    startOAuthFlow() {
      // build 時に env 未設定だと client_id が空のURLを組み立ててLINEが400を返すため、
      // OAuth URL 組立前に early-return する (menoah-mobile-app-flutter#365)
      const channelId = process.env.VUE_APP_LINE_LOGIN_CHANNEL_ID
      const origin = process.env.VUE_APP_WEB_ORIGIN
      if (!channelId || !origin) {
        this.status = 'invalid'
        this.errorMessage = 'サイト設定に問題があります。管理者にお問い合わせください。'
        return
      }
      const q = this.$route.query.q
      const r = this.$route.query.r
      if (!q || !r || !/^[0-9a-f]{64}$/.test(r)) {
        this.status = 'invalid'
        this.errorMessage = '招待リンクが正しくありません。'
        return
      }
      const nonce = this.generateNonce()
      // LINE 内ブラウザは OAuth 往復で sessionStorage を初期化するため (#12)、
      // tokenId(r) と inviterUid(q) を state に載せて URL 経由で確実に持ち回る。
      // LINE 仕様: state は英数字のみ・URLエンコード不可 → 16進文字列で組む。
      // 並び: nonce(32hex) + tokenId(64hex) + inviterUid(16進)
      const state = nonce + r + this.toHex(String(q))
      // CSRF 照合用の nonce は cookie に退避 (best-effort)。消えても tokenId 秘匿で担保。
      this.saveCsrfNonce(nonce)
      window.location.replace(this.buildLineAuthUrl(state))
    },
    toHex(str) {
      let out = ''
      for (let i = 0; i < str.length; i++) {
        // Firebase UID は ASCII 英数字想定。下位8bitで符号化し 2桁/byte を保証。
        out += (str.charCodeAt(i) & 0xff).toString(16).padStart(2, '0')
      }
      return out
    },
    saveCsrfNonce(nonce) {
      try {
        document.cookie = `share_csrf_nonce=${nonce}; Max-Age=3600; Path=/; SameSite=Lax; Secure`
      } catch (e) {
        // cookie 不可でも tokenId は state 経由で渡るため致命ではない
      }
    },
    generateNonce() {
      if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID().replace(/-/g, '')
      }
      const arr = new Uint8Array(16)
      window.crypto.getRandomValues(arr)
      return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('')
    },
    buildLineAuthUrl(state) {
      const channelId = process.env.VUE_APP_LINE_LOGIN_CHANNEL_ID
      const origin = process.env.VUE_APP_WEB_ORIGIN
      const redirectUri = `${origin}/share/callback`
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: channelId,
        redirect_uri: redirectUri,
        state: state,
        scope: 'profile openid',
        // Linked Bot (LINE 公式アカウント) を OAuth 後に友だち追加するよう促す。
        // aggressive = 認可後に専用「友だち追加」画面を表示。
        // 未指定だと友だち追加が促されず、Push 通知が silent drop される問題があるため必須。
        bot_prompt: 'aggressive'
      })
      return `https://access.line.me/oauth2/v2.1/authorize?${params.toString()}`
    }
  }
}
</script>

<style scoped>
.state-block { text-align: center; }
.state-block h1 { color: var(--color-green-600); }
.state-block p { color: var(--color-grey-500); margin-top: 1rem; }
.hint { font-size: 14px; color: var(--color-grey-400); }
.mobile { padding: 56px 24px; }
.mobile h1 { font-size: 24px; margin-bottom: 20px; }
.desktop { padding: 120px 80px; max-width: 1120px; margin: 0 auto; }
.desktop h1 { font-size: 28px; padding-bottom: 40px; }
</style>
